import { useCallback } from 'react';
import { useClinical } from '../contexts/ClinicalContext';
import { clinicalDataService } from '../services/clinicalDataService';
import { searchUtils, relationshipUtils, validationUtils } from '../utils/clinicalUtils';

/**
 * Custom hook for clinical data operations
 */
export const useClinicalData = () => {
  const { state, actions } = useClinical();

  // CRUD operations
  const addItem = useCallback((type, parentId, data) => {
    const defaultData = clinicalDataService.getDefaultValues(type);
    const newData = { ...defaultData, ...data };
    actions.addItem(type, parentId, newData);
  }, [actions]);

  const updateItem = useCallback((type, id, updates) => {
    actions.updateItem(type, id, updates);
  }, [actions]);

  const deleteItem = useCallback((type, id) => {
    actions.deleteItem(type, id);
  }, [actions]);

  // Search operations
  const searchItems = useCallback((type, query) => {
    const items = state[type] || [];
    return searchUtils.filterBySearch(items, query);
  }, [state]);

  // Relationship operations
  const getChildren = useCallback((type, parentId) => {
    return relationshipUtils.getChildren(state, type, parentId);
  }, [state]);

  const getParent = useCallback((type, itemId) => {
    return relationshipUtils.getParent(state, type, itemId);
  }, [state]);

  const getHierarchy = useCallback((type, itemId) => {
    return relationshipUtils.getHierarchy(state, type, itemId);
  }, [state]);

  // Validation
  const validateItem = useCallback((type, item) => {
    return validationUtils.validateItem(type, item);
  }, []);

  const getValidationErrors = useCallback((type, item) => {
    return validationUtils.getValidationErrors(type, item);
  }, []);

  // Entity operations
  const getEntitiesByCategory = useCallback((category) => {
    return state.biological_entities?.filter(entity => entity.category === category) || [];
  }, [state.biological_entities]);

  const getEntitiesByPriority = useCallback((priority) => {
    return state.biological_entities?.filter(entity => entity.priority === priority) || [];
  }, [state.biological_entities]);

  const getEntitiesByStatus = useCallback((status) => {
    return state.biological_entities?.filter(entity => entity.detectionStatus === status) || [];
  }, [state.biological_entities]);

  // Statistics
  const getStatistics = useCallback((type) => {
    const items = state[type];
    
    // Handle single object (like company)
    if (items && !Array.isArray(items)) {
      return {
        total: 1,
        active: items.status === 'active' ? 1 : 0,
        inactive: items.status === 'inactive' ? 1 : 0,
        maintenance: items.status === 'maintenance' ? 1 : 0
      };
    }
    
    // Handle arrays
    const itemsArray = Array.isArray(items) ? items : [];
    return {
      total: itemsArray.length,
      active: itemsArray.filter(item => item.status === 'active').length,
      inactive: itemsArray.filter(item => item.status === 'inactive').length,
      maintenance: itemsArray.filter(item => item.status === 'maintenance').length
    };
  }, [state]);

  return {
    // State
    structure: state,
    
    // CRUD operations
    addItem,
    updateItem,
    deleteItem,
    
    // Search operations
    searchItems,
    setSearchQuery: actions.setSearchQuery,
    searchQuery: state.searchQuery,
    
    // Relationship operations
    getChildren,
    getParent,
    getHierarchy,
    
    // Validation
    validateItem,
    getValidationErrors,
    
    // Entity operations
    getEntitiesByCategory,
    getEntitiesByPriority,
    getEntitiesByStatus,
    setEntityFilters: actions.setEntityFilters,
    entityFilters: {
      category: state.entityFilterCategory,
      priority: state.entityFilterPriority,
      resistance: state.entityFilterResistance,
      status: state.entityFilterStatus
    },
    
    // Statistics
    getStatistics,
    
    // Selection
    selectedItem: state.selectedItem,
    setSelectedItem: actions.setSelectedItem
  };
};
